import { useEffect, useMemo } from 'react'
import debounce from 'lodash/debounce'

import { isUploadableScriptData } from '@/components/Event/NewScript/hooks/state/scriptDataUpload'
import {
  Action,
  ALL_ACTIONS,
  StateActions,
} from '@/components/Event/NewScript/hooks/state/stateActions'
import { DRScriptData } from '@/types'

export type ScriptActions = {
  [key in keyof StateActions]: (data?: any) => DRScriptData
}

export type ScriptActionsDeps = {
  uploadState: (data: DRScriptData) => any
  updateCache: (data: DRScriptData) => void
  getState: () => DRScriptData
}

export const createDebouncedScriptUploader = (
  uploadState: (data: DRScriptData) => any,
) => {
  const uploadDebounced = debounce(uploadState, 500, { leading: true })

  return Object.assign(uploadDebounced, {
    cancelPendingUpload: uploadDebounced.cancel,
  })
}

export const executeScriptAction = (
  action: Action,
  data: any,
  getState: () => DRScriptData,
  updateCache: (data: DRScriptData) => void,
  uploadState: (data: DRScriptData) => any,
): DRScriptData => {
  const newState = action(getState(), data)
  updateCache(newState)

  // A rejected draft save is worse than a late one: the reporter keeps working
  // against the cache, and the next change that leaves the state saveable
  // uploads it.
  if (isUploadableScriptData(newState)) {
    uploadState(newState)
  }

  return newState
}

// Update the React Query cache synchronously so derived values (e.g.
// canContinue) see fresh state immediately, while callers may debounce the
// uploader to batch rapid form changes.
//
// Each action reads the current cache value rather than a render snapshot, so
// several actions dispatched within one event handler compose instead of the
// last one winning.
export const createScriptActions = ({
  getState,
  updateCache,
  uploadState,
}: ScriptActionsDeps): ScriptActions => {
  const result = {} as ScriptActions

  for (const [key, action] of Object.entries(ALL_ACTIONS)) {
    result[key as keyof ScriptActions] = (data: any) =>
      executeScriptAction(action, data, getState, updateCache, uploadState)
  }

  return result
}

export const useScriptActions = ({
  uploadState,
  updateCache,
  getState,
}: ScriptActionsDeps): {
  actions: ScriptActions
  cancelPendingUpload: () => void
} => {
  const uploadDebounced = useMemo(
    () => createDebouncedScriptUploader(uploadState),
    [uploadState],
  )

  useEffect(
    () => () => {
      uploadDebounced.flush()
    },
    [uploadDebounced],
  )

  return useMemo(() => {
    const actions = createScriptActions({
      getState,
      updateCache,
      uploadState: uploadDebounced,
    })

    return {
      actions,
      cancelPendingUpload: uploadDebounced.cancelPendingUpload,
    }
  }, [getState, updateCache, uploadDebounced])
}
